import { Link, useNavigate } from "react-router-dom";
import { useActionState, useEffect } from "react";
import FormBody from "../../../../components/form-body";
import Label from "../../../../components/label";
import Input from "../../../../components/input";
import FormError from "../../../../components/form-error";
import { Button } from "../../../../components/button";
import toast from "react-hot-toast";
import { createShoppingCompany, State } from "../actions";
import Select from "../../../../components/select";
import { ShoppingCategory } from "../../../../models";

export default function ShoppingCompanyCreateForm({
  categories,
}: {
  categories: ShoppingCategory[];
}) {
  const navigate = useNavigate();
  const initialState: State = {
    message: null,
    success: false,
    errors: {},
    defaultValues: {
      name: "",
      url: "",
      discountCode: "",
    },
  };

  const [state, formAction, isPending] = useActionState(
    createShoppingCompany,
    initialState
  );

  useEffect(() => {
    if (state.success) {
      navigate("/admin/shopping-companies");
      toast.success("Shopping company created successfully");
    }
  }, [state, navigate]);

  return (
    <form className="max-w-3xl space-y-6 py-8 text-left" action={formAction}>
      <FormBody>
        <div className="mb-4">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter company name"
            aria-describedby="name-error"
            defaultValue={state.defaultValues.name ?? ""}
            required
          />
          <FormError id="name-error" message={state.errors?.name} />
        </div>

        <div className="mb-4">
          <Label htmlFor="discountCode">Discount code</Label>
          <Input
            id="discountCode"
            name="discountCode"
            type="text"
            placeholder="Enter discount code"
            aria-describedby="discountCode-error"
            defaultValue={state.defaultValues.discountCode ?? ""}
          />
          <FormError id="discountCode-error" message={state.errors?.discountCode} />
        </div>

        <div className="mb-4">
          <Label htmlFor="shoppingCategoryId">Category</Label>
          <Select
            id="shoppingCategoryId"
            name="shoppingCategoryId"
            placeholder="Select category"
            aria-describedby="shoppingCategoryId-error"
            defaultValue={state.defaultValues.shoppingCategoryId ?? ""}
            items={categories
              .filter((category) => category.id != null)
              .map((category) => ({
                id: String(category.id),
                name: category.name ?? "",
              }))}
            required
          />
          <FormError id="shoppingCategoryId-error" message={state.errors?.shoppingCategoryId} />
        </div>

        <div className="mb-4">
          <Label htmlFor="url">Url</Label>
          <Input
            id="url"
            name="url"
            type="url"
            placeholder="https://example.com"
            aria-describedby="url-error"
            defaultValue={state.defaultValues.url ?? ""}
          />
          <FormError id="url-error" message={state.errors?.url} />
        </div>

        <div className="mb-4">
          <Label htmlFor="logoFile">Logo</Label>
          <Input
            id="logoFile"
            name="logoFile"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-describedby="logo-error"
          />
          <FormError id="logo-error" message={state.errors?.logo} />
        </div>

        <FormError id="general-error" message={state.errors?.general} />
      </FormBody>

      <div className="mt-6 flex justify-end gap-4">
        <Link to="/admin/shopping-companies">
          <Button type="reset" disabled={isPending}>
            Cancel
          </Button>
        </Link>
        <Button
          type="submit"
          disabled={isPending}
          loading={isPending}
          className="min-w-[90px]"
        >
          Save Changes
        </Button>
      </div>
    </form>
  );
}
